import {
  Badge,
  Button,
  cn,
  type FileUploadData,
  fileExtensionFromMimeType,
  getFileIcon,
  Icon,
  parseFilename,
  Spinner,
} from "@qw/ui";
import { type FC, useCallback, useMemo } from "react";

import { useChatForm } from "./use-chat-form";

export const ChatFormFilesList: FC = () => {
  const { files, removeFile } = useChatForm();

  const handleRemove = useCallback((index: number) => removeFile(index), [removeFile]);

  if (!files?.length) {
    return null;
  }

  return (
    <ul className="p-1.5 pb-0 w-full min-w-0 flex flex-wrap gap-1">
      {files.map((file, index) => (
        <ChatFormFilesListItem key={file.id} file={file} onRemove={() => handleRemove(index)} />
      ))}
    </ul>
  );
};

interface ChatFormFilesListItemProps {
  file: FileUploadData;
  onRemove: (file: FileUploadData) => void;
}

const ChatFormFilesListItem: FC<ChatFormFilesListItemProps> = ({ file, onRemove }) => {
  const { canEdit } = useChatForm();

  const { name: filename } = parseFilename(file.filename);
  const extension = fileExtensionFromMimeType(file.mimeType);
  const icon = getFileIcon(file.mimeType);

  const isUploading = useMemo(() => file.id.startsWith("temp_"), [file.id]);

  return (
    <li className="max-w-full">
      <Button
        size="xs"
        variant="outline"
        type="button"
        disabled={!canEdit}
        className={cn(
          "z-1 hover:z-5 relative max-w-full pl-1.25 pr-1.25 py-1 gap-1.5 border-border hover:border-input group",
          { "opacity-50": isUploading }
        )}
      >
        <span className="size-3 flex-0">
          {isUploading ? (
            <Spinner className="size-3" />
          ) : (
            <Icon icon={icon} className="text-sm text-foreground opacity-50 filled" />
          )}
        </span>

        <span className="text-xs text-foreground truncate flex-1">{filename}</span>
        <Badge variant="secondary" size="2xs" type="square" className="uppercase flex-none -mr-0.5">
          {extension}
        </Badge>
        <Button
          type="button"
          variant="outline"
          size="2xs"
          display="icon"
          onClick={() => onRemove(file)}
          asChild
          className="rounded-full absolute -top-2 -right-2 invisible group-hover:visible"
        >
          <span>
            <Icon icon="close" />
          </span>
        </Button>
      </Button>
    </li>
  );
};
